import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Building2, Plug, CheckCircle2, XCircle, Loader2, Save, Trash2, RotateCcw,
  AlertTriangle, Lock, ShieldCheck, Database, History, Play, Eye, Check, Rocket,
  FlaskConical, FileText, Dumbbell, Search, Download,
} from "lucide-react";
import { NextFitAnalysisReport, AnalysisJobState } from "@/components/NextFitAnalysisReport";
import { NextFitPlanMapping } from "@/components/NextFitPlanMapping";
import { NextFitFinanceSync } from "@/components/NextFitFinanceSync";
import { NextFitFinanceAudit } from "@/components/NextFitFinanceAudit";
import { NextFitSalesAudit } from "@/components/NextFitSalesAudit";
import { NextFitReactivation } from "@/components/NextFitReactivation";
import { NextFitFutureValidityFix } from "@/components/NextFitFutureValidityFix";
import { NextFitNoCpfImport } from "@/components/NextFitNoCpfImport";
import { NextFitFullMigration } from "@/components/NextFitFullMigration";
import { NextFitMissingActive } from "@/components/NextFitMissingActive";

// Mapa de badges para o status da migração
const STATUS_LABELS = {
  not_migrated: { label: "Não migrada", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  analyzed: { label: "Analisada", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  in_progress: { label: "Em andamento", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  done: { label: "Concluída", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  done_with_errors: { label: "Concluída com erros", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

export default function MigracaoNextFit() {
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [fallbackEnv, setFallbackEnv] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState(null);

  const [configLoading, setConfigLoading] = useState(false);
  const [config, setConfig] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ api_key: "", base_url: "", version: "1", label: "", enabled: true, mock_mode: false });
  const [savingCfg, setSavingCfg] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [imported, setImported] = useState(null);
  const [logs, setLogs] = useState([]);

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeJob, setAnalyzeJob] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateJob, setMigrateJob] = useState(null);

  // Workout audit (READ-ONLY)
  const [activeTab, setActiveTab] = useState("simples"); // "simples" | "migracao" | "treinos"
  const [workoutAuditRunning, setWorkoutAuditRunning] = useState(false);
  const [workoutAuditJob, setWorkoutAuditJob] = useState(null);
  const [workoutAuditReport, setWorkoutAuditReport] = useState(null);
  const [workoutAuditLoaded, setWorkoutAuditLoaded] = useState(false);
  const [workoutFilter, setWorkoutFilter] = useState("all"); // all | available | exists | not_found

  const selectedUnit = useMemo(() => units.find((u) => u.unit_id === selectedUnitId) || null, [units, selectedUnitId]);

  const loadUnits = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/nextfit/units");
      setUnits(data.units || []);
      setFallbackEnv(!!data.fallback_env_available);
      if (!selectedUnitId && data.units?.length) setSelectedUnitId(data.units[0].unit_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar unidades");
    } finally { setLoading(false); }
  };

  const loadUnitData = async (unitId) => {
    if (!unitId) return;
    setConfigLoading(true);
    setConfig(null); setTestResult(null); setPreview(null); setImported(null); setLogs([]);
    try {
      const [cfg, st, im, lg] = await Promise.all([
        api.get(`/integrations/nextfit/units/${unitId}/config`),
        api.get(`/integrations/nextfit/units/${unitId}/migration-status`),
        api.get(`/integrations/nextfit/units/${unitId}/imported`),
        api.get(`/integrations/nextfit/units/${unitId}/logs?limit=30`),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
      setImported(im.data);
      setLogs(lg.data.logs || []);
      setForm({
        api_key: "",
        base_url: cfg.data.base_url || "",
        version: cfg.data.version || "1",
        label: cfg.data.label || "",
        enabled: cfg.data.enabled ?? true,
        mock_mode: !!cfg.data.mock_mode,
      });
      setEditing(!cfg.data.has_config);
      // Prévia (opcional — pode não existir)
      try {
        const pv = await api.get(`/integrations/nextfit/units/${unitId}/preview`);
        setPreview(pv.data.preview ? pv.data : null);
        const aj = pv.data.analysis_job;
        setAnalyzeJob(aj || null);
        setMigrateJob(pv.data.migration_job || null);
        if (aj && (aj.status === "running" || aj.status === "pending")) resumeAnalyze(aj.job_id, unitId);
      } catch { setPreview(null); setAnalyzeJob(null); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar dados da unidade");
    } finally { setConfigLoading(false); }
  };

  const loadWorkoutAuditLast = async (unitId) => {
    try {
      const { data } = await api.get(`/integrations/nextfit/units/${unitId}/workout-audit/last`);
      if (data.has_report) {
        setWorkoutAuditReport(data.report);
      } else {
        setWorkoutAuditReport(null);
      }
      setWorkoutAuditLoaded(true);
    } catch (e) {
      setWorkoutAuditReport(null);
      setWorkoutAuditLoaded(true);
    }
  };

  const runWorkoutAudit = async () => {
    if (!window.confirm("Executar auditoria READ-ONLY do módulo de treinos do Next Fit? NÃO altera nenhum dado.")) return;
    setWorkoutAuditRunning(true); setWorkoutAuditJob(null);
    try {
      const { data } = await api.post(`/integrations/nextfit/units/${selectedUnitId}/workout-audit`);
      setWorkoutAuditJob({ job_id: data.job_id, status: "pending" });
      // Polling
      for (let i = 0; i < 240; i++) {
        try {
          const { data: st } = await api.get(`/integrations/nextfit/workout-audit/${data.job_id}`);
          setWorkoutAuditJob(st);
          if (st.status === "done") {
            setWorkoutAuditReport(st.result);
            toast.success("Auditoria concluída");
            break;
          }
          if (st.status === "error") {
            toast.error(st.error?.detail || "Falha na auditoria");
            break;
          }
        } catch (e) {
          toast.error("Erro ao consultar job");
          break;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar auditoria");
    } finally {
      setWorkoutAuditRunning(false);
    }
  };

  const downloadWorkoutAuditReport = () => {
    if (!workoutAuditReport) return;
    const blob = new Blob([JSON.stringify(workoutAuditReport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nextfit_workout_audit_${selectedUnit?.unit_name || selectedUnitId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => { loadUnits(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (selectedUnitId) {
      loadUnitData(selectedUnitId);
      setWorkoutAuditReport(null);
      setWorkoutAuditLoaded(false);
      setWorkoutAuditJob(null);
      loadWorkoutAuditLast(selectedUnitId);
    }
    /* eslint-disable-next-line */
  }, [selectedUnitId]);

  const saveConfig = async () => {
    if (!selectedUnitId) return;
    if (!config?.has_config && !form.api_key.trim() && !form.mock_mode) {
      toast.error("Informe a chave Next Fit OU ative o modo Mock antes de salvar.");
      return;
    }
    setSavingCfg(true);
    try {
      const payload = {
        base_url: form.base_url || "", version: form.version || "1",
        label: form.label || "", enabled: form.enabled, mock_mode: form.mock_mode,
      };
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      await api.put(`/integrations/nextfit/units/${selectedUnitId}/config`, payload);
      toast.success("Configuração salva");
      setEditing(false); setForm((f) => ({ ...f, api_key: "" }));
      await loadUnitData(selectedUnitId); await loadUnits();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
    finally { setSavingCfg(false); }
  };

  const deleteConfig = async () => {
    if (!window.confirm("Remover a chave Next Fit desta unidade? Alunos migrados NÃO são apagados.")) return;
    try {
      await api.delete(`/integrations/nextfit/units/${selectedUnitId}/config`);
      toast.success("Credencial removida"); await loadUnitData(selectedUnitId); await loadUnits();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao remover"); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post(`/integrations/nextfit/units/${selectedUnitId}/test`);
      const okFlag = data.ok !== false;
      setTestResult({ status: okFlag ? "ok" : "fail",
        message: okFlag
          ? `Conectado · ${data.received_items ?? 0} registro(s) via ${data.endpoint_used || "?"}`
          : `Nenhum endpoint respondeu (${(data.attempts || []).length} tentativas)`,
        sample_fields: data.sample_fields || [], at: new Date().toISOString() });
      if (okFlag) toast.success("Next Fit: conectado"); else toast.error("Next Fit: nenhum endpoint válido");
    } catch (e) {
      setTestResult({ status: "fail", message: e.response?.data?.detail || "Falha ao conectar", at: new Date().toISOString() });
      toast.error("Next Fit: desconectado");
    } finally { setTesting(false); }
  };

  const pollJob = async (jobId, onDone, pollMs = 3000, maxTicks = 1500, onTick) => {
    for (let i = 0; i < maxTicks; i++) {
      try {
        const { data } = await api.get(`/integrations/nextfit/jobs/${jobId}`);
        onTick?.(data);
        if (["done", "error", "failed", "interrupted"].includes(data.status)) {
          onDone(data); return;
        }
      } catch (e) {
        if (e.response?.status === 404) { onDone({ job_id: jobId, status: "interrupted", error: { detail: "Análise não encontrada no servidor — rode 'Analisar' novamente." } }); return; }
      }
      await new Promise((r) => setTimeout(r, pollMs));
    }
    onDone({ job_id: jobId, status: "running", error: { detail: "A análise continua no servidor. Recarregue a página para acompanhar." } });
  };

  const resumeAnalyze = async (jobId, unitId) => {
    setAnalyzing(true);
    await pollJob(jobId, async (final) => {
      setAnalyzeJob(final);
      if (final.status === "done") toast.success("Análise concluída");
      else if (final.status !== "running") toast.error(final.error?.detail || "Falha na análise");
      setAnalyzing(false);
      if (final.status !== "running") { await loadUnitData(unitId); await loadUnits(); }
    }, 3000, 1500, setAnalyzeJob);
  };

  const runAnalyze = async () => {
    setAnalyzing(true); setAnalyzeJob(null);
    try {
      const { data } = await api.post(`/integrations/nextfit/units/${selectedUnitId}/analyze`);
      setAnalyzeJob({ job_id: data.job_id, status: "pending" });
      await resumeAnalyze(data.job_id, selectedUnitId);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar análise"); setAnalyzing(false); }
  };

  const runConfirm = async (confirm = true) => {
    setConfirming(true);
    try {
      await api.post(`/integrations/nextfit/units/${selectedUnitId}/confirm`, { confirm });
      toast.success(confirm ? "Confirmação registrada" : "Confirmação removida");
      await loadUnitData(selectedUnitId); await loadUnits();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao confirmar"); }
    finally { setConfirming(false); }
  };

  const runMigrate = async () => {
    if (!window.confirm("Executar a migração agora? O sistema fará uma NOVA leitura completa da Next Fit e só gravará se não houver inconsistências críticas.")) return;
    const expected = `MIGRAR ${(selectedUnit?.unit_name || "").toUpperCase()}`;
    const phrase = window.prompt(`Autorização final: digite exatamente\n${expected}`);
    if (!phrase) return;
    setMigrating(true); setMigrateJob(null);
    try {
      const { data } = await api.post(`/integrations/nextfit/units/${selectedUnitId}/migrate`, { confirm_phrase: phrase });
      setMigrateJob({ job_id: data.job_id, status: "pending" });
      await pollJob(data.job_id, async (final) => {
        setMigrateJob(final);
        if (final.status === "done") toast.success("Migração concluída");
        else toast.error(final.error?.detail || "Falha na migração");
        await loadUnitData(selectedUnitId); await loadUnits();
      }, 3000, 2400, setMigrateJob);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar migração"); }
    finally { setMigrating(false); }
  };

  const runReset = async () => {
    if (!window.confirm("Resetar análise/confirmação desta unidade? Dados importados são mantidos.")) return;
    try {
      await api.post(`/integrations/nextfit/units/${selectedUnitId}/reset`);
      toast.success("Status resetado"); await loadUnitData(selectedUnitId); await loadUnits();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no reset"); }
  };

  // Gating
  const hasConfig = !!config?.has_config || !!config?.mock_mode;
  const canAnalyze = hasConfig && !analyzing;
  const canPreview = !!preview;
  const migrationAllowed = !!preview?.validity?.migration_allowed;
  const canConfirm = migrationAllowed && !analyzing && !status?.confirmation_at;
  const isConfirmed = !!status?.confirmation_at;
  const canMigrate = isConfirmed && migrationAllowed && !migrating;

  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="migracao-nextfit-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
            <Database className="w-7 h-7 text-rose-600"/>
          </div>
          <div>
            <h1 className="font-heading text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Central de Migração Next Fit
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
              Migração por unidade — cada academia tem sua própria credencial e histórico isolado. Idempotente: rodar duas vezes não duplica.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadUnits} data-testid="refresh-units-btn">
          <RotateCcw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}/>Atualizar
        </Button>
      </div>

      {fallbackEnv && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="fallback-banner">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <span>Credencial Next Fit global detectada no ambiente. Ela só vale para auditorias globais — cada unidade usa sua credencial própria aqui.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <Card className="p-3 h-fit lg:sticky lg:top-4" data-testid="units-list">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 px-2">Unidades ({units.length})</div>
          {loading && <div className="p-4 text-center text-xs text-slate-500"><Loader2 className="w-4 h-4 mx-auto animate-spin mb-1"/>Carregando...</div>}
          <div className="space-y-1">
            {units.map((u) => {
              const st = STATUS_LABELS[u.migration_status] || STATUS_LABELS.not_migrated;
              const isSel = selectedUnitId === u.unit_id;
              return (
                <button key={u.unit_id} onClick={() => setSelectedUnitId(u.unit_id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${isSel ? "bg-rose-600 text-white border-rose-700 shadow-sm" : "bg-white border-slate-200 hover:bg-slate-50 text-slate-900"}`}
                  data-testid={`unit-row-${u.unit_id}`}>
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 shrink-0 ${isSel ? "text-white" : "text-slate-500"}`}/>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm truncate ${isSel ? "text-white" : "text-slate-900"}`}>{u.unit_name}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${isSel ? "bg-white/20 border-white/30 text-white" : st.cls}`}>{st.label}</span>
                        {u.has_config ? <ShieldCheck className={`w-3 h-3 ${isSel ? "text-emerald-200" : "text-emerald-600"}`}/> : <Lock className={`w-3 h-3 ${isSel ? "text-white/60" : "text-slate-400"}`}/>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <div className="space-y-4 min-w-0">
          {!selectedUnit && !loading && <Card className="p-10 text-center text-slate-500">Selecione uma unidade.</Card>}

          {selectedUnit && (
            <>
              {/* Tabs */}
              <div className="flex items-center gap-2 border-b border-slate-200" data-testid="unit-tabs">
                <button
                  onClick={() => setActiveTab("simples")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === "simples" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  data-testid="tab-simples"
                >
                  <Rocket className="w-4 h-4 inline mr-1.5"/>Migrar
                </button>
                <button
                  onClick={() => setActiveTab("migracao")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === "migracao" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  data-testid="tab-migracao"
                >
                  <Database className="w-4 h-4 inline mr-1.5"/>Ferramentas avançadas
                </button>
                <button
                  onClick={() => setActiveTab("treinos")}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === "treinos" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  data-testid="tab-treinos"
                >
                  <Dumbbell className="w-4 h-4 inline mr-1.5"/>Auditoria Treinos
                  <span className="ml-1.5 text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">READ-ONLY</span>
                </button>
              </div>
            </>
          )}

          {selectedUnit && activeTab === "treinos" && (
            <WorkoutAuditTab
              unit={selectedUnit}
              running={workoutAuditRunning}
              job={workoutAuditJob}
              report={workoutAuditReport}
              loaded={workoutAuditLoaded}
              filter={workoutFilter}
              setFilter={setWorkoutFilter}
              onRun={runWorkoutAudit}
              onDownload={downloadWorkoutAuditReport}
            />
          )}

          {selectedUnit && activeTab === "simples" && (
            <NextFitFullMigration unitId={selectedUnit.unit_id} unitName={selectedUnit.unit_name} hasConfig={hasConfig}/>
          )}
          {selectedUnit && activeTab === "simples" && (
            <NextFitMissingActive unitId={selectedUnit.unit_id} hasConfig={hasConfig}/>
          )}

          {selectedUnit && activeTab === "migracao" && (
            <></>
          )}

          {selectedUnit && activeTab === "migracao" && (
            <></>
          )}

          {selectedUnit && activeTab === "migracao" && (
            <>
              {/* Resumo */}
              <Card className="p-5" data-testid="unit-summary">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center"><Building2 className="w-6 h-6 text-slate-700"/></div>
                    <div>
                      <h2 className="font-heading text-xl font-black text-slate-900">{selectedUnit.unit_name}</h2>
                      <div className="text-xs text-slate-500 font-mono">unit_id: {selectedUnit.unit_id}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {config?.mock_mode && <Badge className="bg-purple-100 text-purple-800 border-purple-200 border font-semibold"><FlaskConical className="w-3 h-3 mr-1"/>MOCK MODE</Badge>}
                    <StatusBadge status={status?.status || "not_migrated"}/>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
                  <MetricCell label="Analisados" value={status?.analyzed_count ?? 0}/>
                  <MetricCell label="Alunos migrados" value={imported?.students_from_this_unit ?? 0}/>
                  <MetricCell label="Contratos" value={imported?.contratos ?? 0}/>
                  <MetricCell label="Mensalidades" value={imported?.mensalidades ?? 0}/>
                  <MetricCell label="Erros" value={status?.error_count ?? 0} tone={status?.error_count ? "danger" : null}/>
                </div>
                {(imported?.students_cross_unit_attached ?? 0) > 0 && (
                  <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                    <strong>{imported.students_cross_unit_attached}</strong> aluno(s) reaproveitados de outra unidade via CPF (adicionados a <code>extra_unit_ids</code>).
                  </div>
                )}
              </Card>

              {/* Configuração */}
              <Card className="p-5" data-testid="unit-config-card">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <Plug className="w-5 h-5 text-rose-600"/>
                    <h3 className="font-heading text-lg font-black text-slate-900">Credencial Next Fit</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {config?.has_config && !editing && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-config-btn">Editar</Button>
                        <Button variant="outline" size="sm" onClick={deleteConfig} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="delete-config-btn"><Trash2 className="w-4 h-4 mr-1"/>Remover</Button>
                      </>
                    )}
                    <Button onClick={runTest} disabled={testing || !hasConfig} className="bg-rose-600 hover:bg-rose-700 text-white" size="sm" data-testid="test-config-btn">
                      {testing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Testando...</> : <><Plug className="w-4 h-4 mr-1"/>Testar conexão</>}
                    </Button>
                  </div>
                </div>

                {configLoading && <div className="p-6 text-center text-xs text-slate-500"><Loader2 className="w-4 h-4 mx-auto animate-spin mb-1"/>Lendo...</div>}

                {!configLoading && !editing && config?.has_config && (
                  <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        <ConfigRow label="Modo de operação" value={config.mock_mode ? <Badge className="bg-purple-100 text-purple-800 border-purple-200 border"><FlaskConical className="w-3 h-3 mr-1"/>MOCK (fixtures determinísticas)</Badge> : <span className="text-slate-700">Real (API Next Fit)</span>}/>
                        <ConfigRow label="Chave Next Fit" value={<code className="text-xs font-mono">{config.api_key_masked || "(mock — sem chave)"}</code>}/>
                        <ConfigRow label="URL base" value={<code className="text-xs">{config.base_url || "—"}</code>}/>
                        <ConfigRow label="Versão da API" value={<code className="text-xs">v{config.version}</code>}/>
                        <ConfigRow label="Rótulo interno" value={config.label || <span className="text-slate-400">—</span>}/>
                        <ConfigRow label="Ativa" value={config.enabled ? <span className="text-emerald-700 font-semibold">Sim</span> : <span className="text-rose-700 font-semibold">Não</span>}/>
                        <ConfigRow label="Última alteração" value={config.updated_at ? <span className="text-xs">{new Date(config.updated_at).toLocaleString("pt-BR")} por {config.updated_by || "—"}</span> : "—"}/>
                      </tbody>
                    </table>
                  </div>
                )}

                {!configLoading && (editing || !config?.has_config) && (
                  <div className="space-y-3">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5"/>
                      <span>A chave é <strong>criptografada</strong> com Fernet antes de ir ao banco. Nunca sai em texto puro.</span>
                    </div>
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900 flex items-start gap-2">
                      <FlaskConical className="w-4 h-4 shrink-0 mt-0.5"/>
                      <span><strong>Modo Mock</strong>: usa fixtures determinísticas por <code>unit_id</code> (8 a 14 alunos, contratos, planos e mensalidades) — ideal para validar o fluxo sem bater na API real.</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch id="mock_mode" checked={form.mock_mode} onCheckedChange={(v) => setForm({ ...form, mock_mode: v })} data-testid="switch-mock-mode"/>
                      <Label htmlFor="mock_mode" className="cursor-pointer font-semibold">Modo Mock (sem chamadas à API real)</Label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="api_key">Chave Next Fit (X-Api-Key) {config?.has_config && <span className="text-slate-400 text-xs">— deixe vazio para manter</span>}</Label>
                        <Input id="api_key" type="password" autoComplete="off" placeholder={form.mock_mode ? "(não necessário no modo mock)" : "Cole a chave da unidade"} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} disabled={form.mock_mode} data-testid="input-api-key"/>
                      </div>
                      <div>
                        <Label htmlFor="label">Rótulo interno (opcional)</Label>
                        <Input id="label" placeholder="Ex: Base MONTE ALEGRE" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="input-label"/>
                      </div>
                      <div>
                        <Label htmlFor="base_url">URL base</Label>
                        <Input id="base_url" placeholder="https://integracao.nextfit.com.br" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} disabled={form.mock_mode} data-testid="input-base-url"/>
                      </div>
                      <div>
                        <Label htmlFor="version">Versão da API</Label>
                        <Input id="version" placeholder="1" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} disabled={form.mock_mode} data-testid="input-version"/>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-2">
                      <Switch id="enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="switch-enabled"/>
                      <Label htmlFor="enabled" className="cursor-pointer">Integração ativa</Label>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                      {config?.has_config && <Button variant="outline" onClick={() => { setEditing(false); setForm({ ...form, api_key: "" }); }}>Cancelar</Button>}
                      <Button onClick={saveConfig} disabled={savingCfg} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-config-btn">
                        {savingCfg ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Salvando...</> : <><Save className="w-4 h-4 mr-1"/>Salvar credencial</>}
                      </Button>
                    </div>
                  </div>
                )}

                {testResult && (
                  <div className={`mt-4 rounded-lg border-2 p-4 flex items-start gap-3 ${testResult.status === "ok" ? "border-emerald-300 bg-emerald-50/60" : "border-rose-300 bg-rose-50/60"}`} data-testid="test-result">
                    {testResult.status === "ok" ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5"/> : <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5"/>}
                    <div className="flex-1 min-w-0">
                      <div className={`font-heading text-sm font-black uppercase ${testResult.status === "ok" ? "text-emerald-800" : "text-rose-800"}`}>{testResult.status === "ok" ? "Conectado" : "Desconectado"}</div>
                      <div className="text-xs text-slate-700 mt-0.5">{testResult.message}</div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Fluxo */}
              <Card className="p-5" data-testid="migration-flow-card">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-700"/>
                    <h3 className="font-heading text-lg font-black text-slate-900">Fluxo de migração</h3>
                  </div>
                  <Button variant="outline" size="sm" onClick={runReset} data-testid="reset-btn"><RotateCcw className="w-4 h-4 mr-1"/>Resetar análise</Button>
                </div>
                <div className="grid grid-cols-5 gap-2 text-xs mb-4">
                  <FlowStep n={1} label="Configurar/Testar" active={!hasConfig}/>
                  <FlowStep n={2} label="Analisar" active={hasConfig && !preview}/>
                  <FlowStep n={3} label="Prévia" active={!!preview && !isConfirmed}/>
                  <FlowStep n={4} label="Confirmar" active={!!preview && !isConfirmed}/>
                  <FlowStep n={5} label="Migrar" active={isConfirmed}/>
                </div>

                <div className="flex items-center flex-wrap gap-2">
                  <Button onClick={runAnalyze} disabled={!canAnalyze} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-analyze">
                    {analyzing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Analisando...</> : <><Play className="w-4 h-4 mr-1"/>Analisar</>}
                  </Button>
                  <Button variant="outline" disabled={!canPreview} onClick={() => document.getElementById("preview-section")?.scrollIntoView({ behavior: "smooth" })} data-testid="btn-preview">
                    <Eye className="w-4 h-4 mr-1"/>Ver prévia
                  </Button>
                  {isConfirmed ? (
                    <Button variant="outline" onClick={() => runConfirm(false)} disabled={confirming} data-testid="btn-unconfirm">
                      <XCircle className="w-4 h-4 mr-1"/>Desfazer confirmação
                    </Button>
                  ) : (
                    <Button onClick={() => runConfirm(true)} disabled={!canConfirm || confirming} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-confirm">
                      {confirming ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>...</> : <><Check className="w-4 h-4 mr-1"/>Confirmar prévia</>}
                    </Button>
                  )}
                  <Button onClick={runMigrate} disabled={!canMigrate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-migrate">
                    {migrating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Migrando...</> : <><Rocket className="w-4 h-4 mr-1"/>Migrar</>}
                  </Button>
                </div>

                {!isConfirmed && !canConfirm && !preview && (
                  <div className="mt-3 text-[11px] text-slate-500">Migrar fica liberado apenas após: conexão válida → análise → prévia → confirmação manual.</div>
                )}

                <AnalysisJobState job={analyzeJob}/>

                {migrateJob && (
                  <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="migrate-job-state">
                    <div className="font-mono">Job migração: {migrateJob.job_id?.slice(0, 8)} · status={migrateJob.status}</div>
                    {migrateJob.progress && <div className="font-mono text-slate-700">progress: {JSON.stringify(migrateJob.progress)}</div>}
                    {migrateJob.result && <pre className="text-[10px] mt-1 bg-white rounded p-2 border overflow-auto">{JSON.stringify(migrateJob.result.counts, null, 2)}</pre>}
                    {migrateJob.error && <div className="text-rose-700 font-semibold" data-testid="migrate-job-error">erro: {migrateJob.error.detail}{(migrateJob.error.critical || []).map((c, i) => <div key={i}>• {c.message}</div>)}</div>}
                  </div>
                )}
              </Card>

              {preview?.preview?.plans_table && <NextFitPlanMapping unitId={selectedUnitId} onSaved={() => loadUnitData(selectedUnitId)}/>}
              {selectedUnitId && <NextFitFinanceSync unitId={selectedUnitId} unitName={selectedUnit?.unit_name}/>}
              {selectedUnitId && <NextFitFinanceAudit unitId={selectedUnitId}/>}
              {selectedUnitId && <NextFitSalesAudit unitId={selectedUnitId}/>}
              {selectedUnitId && <NextFitReactivation unitId={selectedUnitId}/>}
              {selectedUnitId && <NextFitFutureValidityFix unitId={selectedUnitId}/>}
              {selectedUnitId && <NextFitNoCpfImport unitId={selectedUnitId}/>}

              {/* Prévia detalhada */}
              {preview && (
                <Card className="p-5" data-testid="preview-section" id="preview-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-5 h-5 text-slate-700"/>
                    <h3 className="font-heading text-lg font-black text-slate-900">Prévia detalhada</h3>
                    {preview.confirmed && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border"><CheckCircle2 className="w-3 h-3 mr-1"/>Confirmada</Badge>}
                  </div>
                  {preview.preview && !preview.validity?.is_current && (
                    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 font-semibold mb-3" data-testid="stale-preview-banner">Esta é a prévia ANTERIOR — a análise mais recente não terminou com sucesso.</div>
                  )}
                  <div className="text-xs text-slate-500 mb-3">
                    Gerada em {preview.preview?.generated_at ? new Date(preview.preview.generated_at).toLocaleString("pt-BR") : "—"} · modo: <strong>{preview.preview?.mock_mode ? "MOCK" : "REAL"}</strong>
                  </div>

                  {/* Regra do dono */}
                  {preview.preview?.destinations && (
                    <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-xs mb-4" data-testid="rule-box">
                      <div className="font-bold text-emerald-900 mb-1">📏 Regra schema-only</div>
                      <div className="text-emerald-900">{preview.preview.destinations.rule}</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">Escreve apenas em:</span>
                        {preview.preview.destinations.collections_written.map(c => <Badge key={c} className="bg-emerald-200 text-emerald-900 border-emerald-300 border"><code>{c}</code></Badge>)}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-rose-800">NÃO cria coleções auxiliares:</span>
                        {preview.preview.destinations.collections_NOT_created.map(c => <code key={c} className="text-[10px] bg-rose-100 text-rose-800 border border-rose-200 rounded px-1">{c}</code>)}
                      </div>
                    </div>
                  )}

                  <NextFitAnalysisReport preview={preview}/>

                  {/* ★★ Campos USADOS vs IGNORADOS ★★ */}
                  {preview.preview.fields_used && (
                    <div className="rounded-lg border-2 border-blue-200 bg-blue-50/50 overflow-hidden mb-3" data-testid="fields-used-box">
                      <div className="px-3 py-2 bg-blue-100 border-b border-blue-200 text-xs font-bold uppercase tracking-wider text-blue-900">
                        ✅ Campos UTILIZADOS (correspondência com schema American Fit)
                      </div>
                      <div className="p-3 space-y-3 text-xs">
                        {Object.entries(preview.preview.fields_used).map(([entity, fields]) => (
                          <div key={entity}>
                            <div className="font-bold text-slate-800 mb-1">→ coleção <code>{entity}</code></div>
                            <table className="w-full">
                              <thead>
                                <tr className="text-[10px] uppercase text-slate-500">
                                  <th className="text-left px-2 py-1 w-1/3">Campo American Fit</th>
                                  <th className="text-left px-2 py-1">Origem Next Fit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(fields).map(([k, v]) => (
                                  <tr key={k} className="border-t border-blue-100">
                                    <td className="px-2 py-1 font-mono text-blue-900">{k}</td>
                                    <td className="px-2 py-1 text-slate-700">{v}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.preview.fields_ignored && (
                    <div className="rounded-lg border-2 border-slate-300 bg-slate-50 overflow-hidden mb-3" data-testid="fields-ignored-box">
                      <div className="px-3 py-2 bg-slate-200 border-b border-slate-300 text-xs font-bold uppercase tracking-wider text-slate-700">
                        🚫 Campos IGNORADOS por entidade (sem destino no schema American Fit)
                      </div>
                      <div className="p-3 space-y-2 text-xs">
                        {Object.entries(preview.preview.fields_ignored).map(([entity, fields]) => (
                          <div key={entity}>
                            <div className="font-bold text-slate-800">{entity}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {fields.map(f => <code key={f} className="text-[10px] bg-white border border-slate-200 rounded px-1 py-0.5 text-slate-700">{f}</code>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {preview.preview.ignored_entities && (
                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-3" data-testid="ignored-entities-box">
                      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-600">
                        🚫 Entidades Next Fit IGNORADAS por completo
                      </div>
                      <div className="p-3 space-y-2 text-xs">
                        {preview.preview.ignored_entities.map(e => (
                          <div key={e.entity} className="border-l-2 border-slate-300 pl-2">
                            <div className="font-bold text-slate-800">{e.entity}</div>
                            <div className="text-slate-600">{e.reason}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Amostras */}
                  <SampleSection title={`Amostra de alunos (${preview.preview.samples?.clientes?.length ?? 0})`} rows={preview.preview.samples?.clientes || []}/>
                </Card>
              )}

              {/* Logs */}
              <Card className="p-5" data-testid="logs-card">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-slate-700"/>
                  <h3 className="font-heading text-base font-black text-slate-900">Logs recentes</h3>
                </div>
                {logs.length === 0 && <div className="text-xs text-slate-500">Sem logs ainda.</div>}
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {logs.map((l, i) => (
                    <div key={i} className="text-[11px] font-mono flex items-start gap-2 border-b border-slate-100 pb-1">
                      <span className="text-slate-400 shrink-0">{new Date(l.at).toLocaleTimeString("pt-BR")}</span>
                      <span className={`uppercase font-bold shrink-0 ${l.level === "error" ? "text-rose-700" : "text-slate-500"}`}>{l.level}</span>
                      <span className="text-slate-800 break-all">{l.message}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.not_migrated;
  return <Badge className={`${s.cls} border font-semibold uppercase tracking-wider text-[10px]`} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
}
function MetricCell({ label, value, small, tone }) {
  const toneCls = tone === "danger" ? "text-rose-700" : "text-slate-900";
  return <div className="rounded-lg border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div><div className={`${small ? "text-xs" : "text-xl"} font-black mt-0.5 ${toneCls}`}>{value}</div></div>;
}
function ConfigRow({ label, value }) {
  return <tr className="border-b border-slate-100 last:border-0"><td className="px-3 py-2 text-slate-600 w-1/2">{label}</td><td className="px-3 py-2 text-right">{value}</td></tr>;
}
function FlowStep({ n, label, active }) {
  return <div className={`rounded-lg border p-2 text-center ${active ? "bg-rose-50 border-rose-300 text-rose-900" : "bg-slate-50 border-slate-200 text-slate-500"}`}><div className="font-black text-sm">{n}</div><div className="text-[10px]">{label}</div></div>;
}
function SummaryBox({ title, data }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs font-bold uppercase text-slate-600 mb-1">{title}</div>
      <div className="text-[11px] space-y-0.5">
        {Object.entries(data || {}).map(([k, v]) => {
          if (typeof v === "object" && v !== null) {
            return (
              <div key={k}><span className="text-slate-500">{k}:</span>
                <ul className="list-disc list-inside ml-2 text-slate-700">
                  {Object.entries(v).map(([k2, v2]) => <li key={k2}>{k2}: <code>{String(v2)}</code></li>)}
                </ul>
              </div>
            );
          }
          return <div key={k}><span className="text-slate-500">{k}:</span> <code className="text-slate-800">{String(v)}</code></div>;
        })}
      </div>
    </div>
  );
}
function DedupeStat({ label, value, tone }) {
  const map = { emerald: "text-emerald-700", blue: "text-blue-700", amber: "text-amber-700", rose: "text-rose-700" };
  return <div className="rounded border border-slate-200 bg-slate-50 p-2"><div className="text-[10px] uppercase text-slate-500">{label}</div><div className={`text-lg font-black ${map[tone] || "text-slate-900"}`}>{value}</div></div>;
}
function SampleSection({ title, rows }) {
  if (!rows?.length) return null;
  const cols = Object.keys(rows[0]);
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden mb-3">
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-600">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] font-mono">
          <thead><tr>{cols.map((c) => <th key={c} className="px-2 py-1 bg-slate-50 border-b text-left text-slate-600">{c}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0 border-slate-100">
                {cols.map((c) => <td key={c} className="px-2 py-1 text-slate-800 whitespace-nowrap">{r[c] === null || r[c] === undefined ? "—" : typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Workout Audit Tab — Auditoria READ-ONLY do módulo de treinos do Next Fit
// ============================================================================
function WorkoutAuditTab({ unit, running, job, report, loaded, filter, setFilter, onRun, onDownload }) {
  const summary = report?.summary || {};
  const baseline = report?.baseline_check || {};
  const swagger = report?.swagger_discovery || {};
  const endpoints = report?.endpoints || [];
  const matrix = report?.matrix || [];

  const filtered = endpoints.filter((ep) => {
    if (ep.category === "_baseline") return false;
    if (filter === "all") return true;
    if (filter === "available") return ["available", "available_empty"].includes(ep.status_label);
    if (filter === "exists") return (ep.status_label || "").startsWith("exists_") || ep.status_label === "rate_limited" || ep.status_label === "server_error";
    if (filter === "not_found") return ep.status_label === "not_found";
    return true;
  });

  return (
    <div className="space-y-4" data-testid="workout-audit-tab">
      {/* Alerta regra */}
      <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-4 text-sm text-blue-900 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5 text-blue-700"/>
        <div>
          <div className="font-bold text-blue-900 mb-1">Auditoria 100% READ-ONLY</div>
          <div className="text-xs">
            Esta auditoria apenas <strong>LÊ</strong> endpoints do Next Fit (HTTP GET). Nenhum dado é alterado no Next Fit nem gravado no American Fit.
            Valores de campos sensíveis (nome/CPF/email/telefone/endereço) são automaticamente mascarados.
            Regra schema-only: só migramos para campos JÁ EXISTENTES no American Fit.
          </div>
        </div>
      </div>

      {/* Comando */}
      <Card className="p-5" data-testid="workout-audit-command-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center"><Dumbbell className="w-6 h-6 text-purple-700"/></div>
            <div>
              <h2 className="font-heading text-xl font-black text-slate-900">Auditoria de Treinos · {unit.unit_name}</h2>
              <div className="text-xs text-slate-500">Testa ~190 endpoints plausíveis (Treino, Ficha, Exercício, Série, Grupo Muscular, Prescrição, Programa, Rotina, Professor, Mídia...) + baixa o Swagger oficial.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {report && (
              <Button variant="outline" size="sm" onClick={onDownload} data-testid="btn-download-workout-report">
                <Download className="w-4 h-4 mr-1"/>Baixar JSON
              </Button>
            )}
            <Button onClick={onRun} disabled={running} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-run-workout-audit">
              {running ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Auditando...</> : <><Search className="w-4 h-4 mr-1"/>Executar auditoria</>}
            </Button>
          </div>
        </div>
        {job && job.status !== "done" && (
          <div className="mt-3 text-xs text-slate-600 font-mono" data-testid="workout-audit-progress">
            job {job.job_id?.slice(0,8)} · {job.status} · {job.progress?.phase || "init"} · {job.progress?.done ?? 0}/{job.progress?.total ?? 0}
            {job.progress?.last_path && <span className="text-slate-400 ml-2">→ {job.progress.last_path} ({job.progress.last_status || "?"})</span>}
          </div>
        )}
      </Card>

      {/* Loading state */}
      {!report && loaded && !running && (
        <Card className="p-8 text-center text-slate-500 text-sm" data-testid="workout-audit-empty">
          Nenhuma auditoria executada ainda para esta unidade. Clique em <strong>Executar auditoria</strong> para começar.
        </Card>
      )}

      {report && (
        <>
          {/* Baseline */}
          <Card className="p-4" data-testid="workout-audit-baseline">
            <div className="flex items-center gap-2 mb-2">
              {baseline.passed ? <CheckCircle2 className="w-5 h-5 text-emerald-600"/> : <XCircle className="w-5 h-5 text-rose-600"/>}
              <h3 className="font-heading text-base font-black text-slate-900">
                Baseline (sanity check): {baseline.passed ? "PASSOU" : "FALHOU"}
              </h3>
            </div>
            <div className="text-xs text-slate-500 mb-2">{baseline.note}</div>
            <div className="grid md:grid-cols-2 gap-2">
              {(baseline.endpoints || []).map((ep) => (
                <div key={ep.path} className={`rounded border p-2 text-xs flex items-center gap-2 ${ep.status_code === 200 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                  <code className="font-mono font-bold">{ep.status_code}</code>
                  <code className="text-slate-700 flex-1">{ep.path}</code>
                  <span className="text-slate-500">batch={ep.schema?.batch_length ?? "—"}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Swagger oficial */}
          <Card className="p-4" data-testid="workout-audit-swagger">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-slate-700"/>
              <h3 className="font-heading text-base font-black text-slate-900">Swagger oficial do Next Fit</h3>
              {swagger.found ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]">ENCONTRADO</Badge> : <Badge className="bg-slate-100 text-slate-600 border-slate-200 border text-[10px]">NÃO EXPOSTO</Badge>}
            </div>
            {swagger.found ? (
              <div className="text-xs space-y-2">
                <div><span className="text-slate-500">Fonte:</span> <code>{swagger.source}</code></div>
                <div><span className="text-slate-500">Total de paths expostos:</span> <strong className="text-slate-900">{swagger.total_paths}</strong></div>
                <div><span className="text-slate-500">Paths que casam com termos de treino:</span> {swagger.paths_matched_workout?.length === 0 ? <span className="text-rose-700 font-bold">NENHUM</span> : <strong>{swagger.paths_matched_workout?.length}</strong>}</div>
                {swagger.sample_paths_first_50?.length > 0 && (
                  <div>
                    <div className="font-semibold text-slate-700 mt-2 mb-1">Todos os {swagger.total_paths} endpoints oficiais da API Next Fit:</div>
                    <div className="flex flex-wrap gap-1">
                      {swagger.sample_paths_first_50.map((p) => (
                        <code key={p} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-800 font-mono">{p}</code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-slate-500">Swagger não exposto nesta instância — a conclusão baseia-se apenas nos probes manuais.</div>
            )}
          </Card>

          {/* Summary */}
          <Card className="p-4" data-testid="workout-audit-summary">
            <h3 className="font-heading text-base font-black text-slate-900 mb-2">Resumo dos probes</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <StatPill label="Total testados" value={report.total_endpoints_tested}/>
              <StatPill label="200 Disponível" value={summary.available ?? 0} tone="emerald"/>
              <StatPill label="Existe (401/403/400/405)" value={(summary.exists_bad_request||0)+(summary.exists_unauthorized||0)+(summary.exists_forbidden||0)+(summary.exists_wrong_method||0)+(summary.exists_unprocessable||0)} tone="amber"/>
              <StatPill label="404 Not found" value={summary.not_found ?? 0} tone="slate"/>
            </div>
          </Card>

          {/* Matriz final */}
          <Card className="p-4" data-testid="workout-audit-matrix">
            <h3 className="font-heading text-base font-black text-slate-900 mb-3">
              Matriz de disponibilidade (Dado Next Fit → Endpoint → Campo → Pode migrar?)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase text-slate-600">
                    <th className="text-left px-2 py-2">Dado Next Fit</th>
                    <th className="text-left px-2 py-2">Endpoint</th>
                    <th className="text-left px-2 py-2">Campo(s) origem</th>
                    <th className="text-left px-2 py-2">Disponível?</th>
                    <th className="text-left px-2 py-2">Campo American Fit</th>
                    <th className="text-left px-2 py-2">Pode migrar?</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, i) => (
                    <tr key={i} className="border-t border-slate-200">
                      <td className="px-2 py-2 font-semibold text-slate-800">{row.dado}</td>
                      <td className="px-2 py-2 font-mono text-slate-700">{row.endpoint}</td>
                      <td className="px-2 py-2 text-slate-600">{row.campo_origem || "—"}</td>
                      <td className="px-2 py-2"><MatrixBadge value={row.disponivel}/></td>
                      <td className="px-2 py-2 text-slate-700">{row.campo_americanfit}</td>
                      <td className="px-2 py-2"><MatrixBadge value={row.pode_migrar}/>{row.nota && <div className="text-[10px] text-slate-500 mt-0.5">{row.nota}</div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Lista de endpoints */}
          <Card className="p-4" data-testid="workout-audit-endpoints">
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <h3 className="font-heading text-base font-black text-slate-900">
                Endpoints testados ({report.total_endpoints_tested})
              </h3>
              <div className="flex items-center gap-1 text-xs">
                {["all","available","exists","not_found"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-2 py-1 rounded border text-[11px] font-mono ${filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`filter-${f}`}
                  >{f}</button>
                ))}
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto border border-slate-100 rounded">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-[10px] uppercase text-slate-600">
                    <th className="text-left px-2 py-1 w-16">Status</th>
                    <th className="text-left px-2 py-1 w-24">Label</th>
                    <th className="text-left px-2 py-1 w-24">Categoria</th>
                    <th className="text-left px-2 py-1">Path</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ep) => (
                    <tr key={ep.path} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1 font-mono font-bold">
                        <span className={ep.status_code === 200 ? "text-emerald-700" : ep.status_code === 404 ? "text-slate-400" : "text-amber-700"}>
                          {ep.status_code ?? "—"}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-slate-600">{ep.status_label}</td>
                      <td className="px-2 py-1 text-slate-500 font-mono">{ep.category}</td>
                      <td className="px-2 py-1 font-mono text-slate-800 break-all">{ep.path}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Legenda: <strong>200</strong>=disponível · <strong>401/403</strong>=existe sem autorização · <strong>400/405</strong>=existe (parâmetros/método inválido) · <strong>404</strong>=não encontrado.
            </div>
          </Card>

          {/* Conclusão (quando não há endpoints 200 de treino) */}
          {(summary.available ?? 0) === 0 && (
            <Card className="p-4 border-2 border-rose-300 bg-rose-50" data-testid="workout-audit-conclusion">
              <h3 className="font-heading text-base font-black text-rose-900 mb-2">
                🔴 Conclusão da auditoria
              </h3>
              <div className="text-sm text-rose-900 space-y-2">
                <p>
                  <strong>Nenhum endpoint de treino foi encontrado</strong> na API de integração oficial do Next Fit para esta unidade ({unit.unit_name}).
                </p>
                <p>
                  O baseline (clientes + contratos) <strong>respondeu 200</strong>, confirmando que a credencial e a infraestrutura HTTP estão corretas — portanto a ausência não é por erro de autenticação ou de rede.
                </p>
                {swagger.found && (
                  <p>
                    O <strong>Swagger oficial</strong> da API foi baixado com sucesso e lista apenas <strong>{swagger.total_paths} endpoints</strong>, nenhum deles relacionado a treino, exercício, ficha, série ou grupo muscular.
                  </p>
                )}
                <p className="text-xs italic">
                  Decisão sugerida: alunos refarão as fichas de treino diretamente no American Fit após a migração dos cadastros/contratos.
                </p>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StatPill({ label, value, tone = "slate" }) {
  const toneMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded border p-2 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[10px] uppercase font-semibold opacity-75">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}

function MatrixBadge({ value }) {
  const v = (value || "").toUpperCase();
  if (v === "SIM") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]">SIM</Badge>;
  if (v === "NÃO" || v === "NAO") return <Badge className="bg-rose-100 text-rose-800 border-rose-200 border text-[10px]">NÃO</Badge>;
  if (v === "ROTA EXISTE") return <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px]">ROTA EXISTE (sem dados)</Badge>;
  return <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px]">{value}</Badge>;
}
